/**
 * Whether this bundle can read a FlatBuffer HTTP body that a server built with
 * another API schema wrote.
 *
 * A response names the server's `Api.ApiSchema` in `X-LN2-Schema`. When that
 * is this bundle's own, nothing more happens. Otherwise the schema API's
 * verdict for the body's type decides (`GET /api/flatbuffers/schemas/{sha256}/verdict`):
 * a type the server's schema keeps as this bundle reads it, or only added to,
 * is read as today. One it changed is refused with a message saying so,
 * instead of being misread. A server that cannot say (it does not know this
 * bundle's schema, say) is read as today. Each meeting is recorded for the
 * admin skew banner.
 *
 * When the server cannot say, the page asks for the server's schema itself and
 * compares the body's type with its own by reflection (`layoutProof`). A type
 * laid out identically in both is recorded as proven locally, which the banner
 * leaves out; anything else stays unknown. The body is read as today either way.
 */

import { sameReachableLayout } from "../discovery/layoutProof";
import { fetchSchema, type FetchedSchema } from "../discovery/schemaFetch";
import { loadSchema, type ReflectionSchema } from "../reflection/reflection";
import { API_SCHEMA_HASH } from "../schemaHashes";
import { LOCAL_PROOF_OUTCOME, noteSchemaSkew } from "../schemaSkew";

/** The schema API's verdict (LiftCloud `controllers.schemaVerdict`). */
export interface SchemaVerdict {
  writer: string;
  writer_provenance: string;
  reader: string;
  type: string;
  /** `identical`, `proven`, `extended`, `diverged` or `unrelated`. */
  kind: string;
  /** The best a body of the type can resolve as: `exact` … `inspect_only`. */
  outcome: string;
  gained_values: { enum: string; union: boolean; values: string[] }[];
  must_understand: { owner: string; name: string; value: boolean }[];
  incompatibilities: { path: string; reason: string; detail: string }[];
}

/**
 * Outcomes whose bodies read the same under this bundle's schema: the server's
 * schema is this one, is proven to decode as it, or only added to it.
 */
const DECODABLE_OUTCOMES = new Set(["exact", "proven", "extended"]);

/**
 * Whether a body the verdict describes can be read with this bundle's own
 * handler. A writer item this bundle must understand and does not refuses
 * every body: a body is not walked here, so one that carries the item cannot
 * be told from one that does not.
 */
export function isDecodable(verdict: SchemaVerdict): boolean {
  return DECODABLE_OUTCOMES.has(verdict.outcome) && verdict.must_understand.length === 0;
}

/** How long a server that could not say is left before it is asked again. */
const RETRY_AFTER_MS = 5 * 60 * 1000;

interface CachedVerdict {
  verdict: Promise<SchemaVerdict | null>;
  /** When to ask again: never, once a verdict came back. */
  expires: number;
}

const verdicts = new Map<string, CachedVerdict>();

/**
 * Whether a 200 reply is a verdict this module can read. Anything else (a
 * proxy's JSON, a body missing the lists `isDecodable` and `refusal` walk) is
 * the server not being able to say, as a failed request is: a TypeError from
 * walking it would otherwise escape the base query as a cut-short read and be
 * resent.
 */
function isVerdict(body: unknown): body is SchemaVerdict {
  if (typeof body !== "object" || body === null) {
    return false;
  }
  const candidate = body as Partial<SchemaVerdict>;
  return (
    typeof candidate.outcome === "string" &&
    Array.isArray(candidate.must_understand) &&
    Array.isArray(candidate.incompatibilities)
  );
}

async function fetchVerdict(
  baseUrl: string,
  writer: string,
  reader: string,
  typeName: string | undefined,
  authorization: string | undefined,
): Promise<SchemaVerdict | null> {
  const query = new URLSearchParams({ reader });
  if (typeName !== undefined) {
    query.set("type", typeName);
  }
  const headers: Record<string, string> = { Accept: "application/json" };
  if (authorization) {
    headers.authorization = authorization;
  }
  try {
    const response = await fetch(
      `${baseUrl}/flatbuffers/schemas/${writer}/verdict?${query.toString()}`,
      { headers },
    );
    if (!response.ok) {
      return null;
    }
    const body: unknown = await response.json();
    return isVerdict(body) ? body : null;
  } catch {
    return null;
  }
}

/**
 * The schema API's verdict for bodies written with `writer` and read with
 * `reader` (both checked SHA-256s), for `typeName` alone when it is given, or
 * null when the server cannot say. A question is asked once; one the server
 * could not answer is asked again after a while.
 */
export function schemaVerdict(
  baseUrl: string,
  writer: string,
  reader: string,
  typeName: string | undefined,
  authorization?: string,
): Promise<SchemaVerdict | null> {
  const key = `${baseUrl}|${writer}|${reader}|${typeName ?? ""}`;
  const cached = verdicts.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.verdict;
  }
  const pending = fetchVerdict(baseUrl, writer, reader, typeName, authorization);
  verdicts.set(key, { verdict: pending, expires: Number.POSITIVE_INFINITY });
  return pending.then((verdict) => {
    if (verdict === null && verdicts.get(key)?.verdict === pending) {
      verdicts.set(key, { verdict: pending, expires: Date.now() + RETRY_AFTER_MS });
    }
    return verdict;
  });
}

/**
 * The verdict for bodies of `typeName` written with `serverSchema` and read
 * with this bundle's API schema, or null when the server cannot say.
 * `serverSchema` must already be a checked SHA-256 (`responseSchema`).
 */
export function apiVerdict(
  baseUrl: string,
  serverSchema: string,
  typeName: string,
  authorization?: string,
): Promise<SchemaVerdict | null> {
  return schemaVerdict(baseUrl, serverSchema, API_SCHEMA_HASH, typeName, authorization);
}

/** What the page learned about the schema it fetched to prove a type. */
interface LocalProof {
  provenance: string;
  signature: FetchedSchema["signature"];
}

interface CachedProof {
  proof: Promise<LocalProof | null>;
  /** When to try again: never, once a type is proven. */
  expires: number;
}

const proofs = new Map<string, CachedProof>();

/** This bundle's own API schema, loaded when a type first needs proving. */
let ownSchema: Promise<ReflectionSchema> | undefined;

function ownApiSchema(): Promise<ReflectionSchema> {
  if (ownSchema === undefined) {
    const loading = import("../generated/api/api-schema-bfbs").then((module) =>
      loadSchema(module.bfbsData),
    );
    ownSchema = loading;
    loading.catch(() => {
      if (ownSchema === loading) {
        ownSchema = undefined;
      }
    });
  }
  return ownSchema;
}

async function proveLocally(
  baseUrl: string,
  writer: string,
  typeName: string,
  authorization: string | undefined,
): Promise<LocalProof | null> {
  try {
    const fetched = await fetchSchema(baseUrl, writer, authorization);
    if (fetched === null) {
      return null;
    }
    const proven = sameReachableLayout(loadSchema(fetched.bfbs), await ownApiSchema(), typeName);
    return proven ? { provenance: fetched.provenance, signature: fetched.signature } : null;
  } catch {
    return null;
  }
}

/**
 * Whether `typeName` is laid out in the server's `writer` schema (fetched from
 * the schema API) exactly as in this bundle's own, or null when that cannot be
 * shown. A proof is kept; one that failed is tried again after a while.
 */
function localProof(
  baseUrl: string,
  writer: string,
  typeName: string,
  authorization: string | undefined,
): Promise<LocalProof | null> {
  const key = `${baseUrl}|${writer}|${typeName}`;
  const cached = proofs.get(key);
  if (cached && cached.expires > Date.now()) {
    return cached.proof;
  }
  const pending = proveLocally(baseUrl, writer, typeName, authorization);
  proofs.set(key, { proof: pending, expires: Number.POSITIVE_INFINITY });
  return pending.then((proof) => {
    if (proof === null && proofs.get(key)?.proof === pending) {
      proofs.set(key, { proof: pending, expires: Date.now() + RETRY_AFTER_MS });
    }
    return proof;
  });
}

function observedOutcome(verdict: SchemaVerdict | null, proof: LocalProof | null): string {
  if (verdict !== null) {
    return verdict.outcome;
  }
  return proof === null ? "unknown" : LOCAL_PROOF_OUTCOME;
}

const SHORT_HASH = 8;
const MAX_REASONS = 3;

function refusal(serverSchema: string, typeName: string, verdict: SchemaVerdict): string {
  const reasons = [
    ...verdict.must_understand.map((item) => `${item.owner}.${item.name} must be understood`),
    ...verdict.incompatibilities.map(
      (finding) => `${finding.path}: ${finding.detail || finding.reason}`,
    ),
  ];
  const why =
    reasons.length > 0
      ? reasons.slice(0, MAX_REASONS).join("; ")
      : `the schemas are ${verdict.kind}`;
  return (
    `${typeName} from this server cannot be read safely by this app: the server's API schema ` +
    `(${serverSchema.slice(0, SHORT_HASH)}) is not this app's (${API_SCHEMA_HASH.slice(0, SHORT_HASH)}), ` +
    `and it changed the type (${verdict.outcome}): ${why}`
  );
}

/**
 * Why a body of `typeName` that the server at `baseUrl` wrote with
 * `serverSchema` (the response's checked `X-LN2-Schema`) cannot be read by this
 * bundle, or null when it can be read as today.
 */
export async function responseSchemaRefusal(
  baseUrl: string,
  serverSchema: string | null,
  typeName: string | undefined,
  authorization?: string,
): Promise<string | null> {
  if (serverSchema === null || serverSchema === API_SCHEMA_HASH || typeName === undefined) {
    return null;
  }
  const verdict = await apiVerdict(baseUrl, serverSchema, typeName, authorization);
  const proof =
    verdict === null ? await localProof(baseUrl, serverSchema, typeName, authorization) : null;
  noteSchemaSkew({
    channel: "http",
    schema: serverSchema,
    typeName,
    outcome: observedOutcome(verdict, proof),
    provenance: verdict?.writer_provenance ?? proof?.provenance,
    signature: proof?.signature,
  });
  if (verdict === null || isDecodable(verdict)) {
    return null;
  }
  return refusal(serverSchema, typeName, verdict);
}

/** Forgets every verdict (for tests). */
export function resetApiVerdicts(): void {
  verdicts.clear();
  proofs.clear();
  ownSchema = undefined;
}
